import "./customer-info.css";

import { useAtom } from "jotai";
import { useCallback, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import {
	getSearchCustomerRoute,
	useQuoteDetailsPageLoadQueries,
} from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { SearchCombobox } from "~/shared/components/fields/search-combobox.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { Customer } from "~/shared/schemas/customer-schema.ts";
import { customerSchema } from "~/shared/schemas/customer-schema.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import type {
	OnChange,
	QuoteStateType,
} from "~/shared/types/quote-details-types";

import {
	blankAddress,
	blankCustomer,
	defaultHandlingUnit,
	initVal,
} from "../../constants/quote-detail-constants.ts";

type CustomerInfoProps = {
	getChangeHandler: (
		field: keyof QuoteStateType,
	) => (change: { value: OnChange }) => void;
	customerCleared: boolean;
	setCustomerCleared: (value: boolean) => void;
	showValidationError?: boolean;
	onClearValidation?: () => void;
	redirectRoute?: string;
	templateMode?: boolean;
	forceExternal?: boolean;
};

export const CustomerInfo = ({
	getChangeHandler,
	customerCleared,
	setCustomerCleared,
	showValidationError = false,
	onClearValidation,
	redirectRoute = routes.quoteDetails,
	templateMode = false,
	forceExternal = false,
}: CustomerInfoProps) => {
	const { isInternal: isInternalDeployment } = useDeploymentType();
	const isInternal = !forceExternal && isInternalDeployment;
	const navigate = useNavigate();
	const { quoteId } = useParams<{ quoteId: string }>();
	const { onChange, quoteState, setCustomerDetails, setQuoteState } =
		useContext(QuoteDetailsContext);
	const { resetSelectedAddressBookItems } = useQuoteAddressContext();
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const [, setSelectedCustomer] = useAtom(selectedCustomerAtom);

	const clearAllShipmentDetails = useCallback(() => {
		resetSelectedAddressBookItems();
		onChange("pickupPostalCode", blankAddress);
		onChange("deliveryPostalCode", blankAddress);
		onChange("items", [defaultHandlingUnit]);
		onChange("totalLinearFeet", 0);
		onChange("customTotalLinearFeet", 0);

		const pickupCommercial = pageData?.accessorials?.locationType?.[
			"pickup"
		]?.find((a: Accessorial) => a.name === "Commercial");
		const deliveryCommercial = pageData?.accessorials?.locationType?.[
			"delivery"
		]?.find((a: Accessorial) => a.name === "Commercial");

		onChange("pickupServices", pickupCommercial ? [pickupCommercial] : []);
		onChange(
			"deliveryServices",
			deliveryCommercial ? [deliveryCommercial] : [],
		);
	}, [resetSelectedAddressBookItems, onChange, pageData]);

	useEffect(() => {
		if (customerCleared) {
			setCustomerDetails(blankCustomer);
			clearAllShipmentDetails();
			setSelectedCustomer(null);
			setQuoteState((prevState) => ({
				...prevState,
				templateId: null,
				templateName: null,
			}));
			setCustomerCleared(false);

			// If revising an existing quote, navigate to the clean quote-details
			// URL (without :quoteId). This removes the quoteId from the URL so
			// that useQuoteLoader's resetForm() fires and user defaults will be
			// applied when a new customer is selected.
			// NOTE: We intentionally do NOT set quoteState.id = 0 here. Doing so
			// before navigate() takes effect would create an intermediate render
			// where quoteState.id (0) !== quoteId (URL), making shouldFetchQuote
			// true and reloading the old quote from cache.
			if (quoteId) {
				navigate(redirectRoute, { replace: true });
			}
		}
	}, [
		customerCleared,
		setCustomerDetails,
		clearAllShipmentDetails,
		setSelectedCustomer,
		setQuoteState,
		setCustomerCleared,
		quoteId,
		navigate,
		redirectRoute,
	]);

	return isInternal ? (
		<div className={"customer-info-wrapper"}>
			<div className={"quick-customer-select"}>
				<h4>Customer Select</h4>
				<SearchCombobox
					dataItemKey={"id"}
					disabled={templateMode}
					handleChange={(event) => {
						if (onClearValidation) onClearValidation();
						// Accept any event type, extract value if present
						const value = (event as { value?: OnChange })?.value;
						const newCustomer = value as Customer;
						const selectedCustomer = {
							...initVal.customer,
							id: newCustomer?.id ?? 0,
						};
						const currentCustomerId = quoteState.customer?.id;
						const hasCustomerChanged =
							Boolean(selectedCustomer.id) &&
							selectedCustomer.id !== currentCustomerId;

						if (hasCustomerChanged && currentCustomerId !== 0) {
							clearAllShipmentDetails();
						}

						// Clear customer-dependent fields so stale data from the
						// previous customer is never displayed while fresh API
						// data is loading.
						if (hasCustomerChanged) {
							setQuoteState((prevState) => ({
								...prevState,
								customerDetails: {
									...initVal.customerDetails,
									id: selectedCustomer.id,
								},
								customerAr: initVal.customerAr,
								customerCredits: {},
							}));
						}
						setSelectedCustomer(selectedCustomer.id ? selectedCustomer : null);
						getChangeHandler("customer")({ value: selectedCustomer });
					}}
					hint={false}
					hintDirection={undefined}
					id={"customer"}
					label={""}
					minSearchLength={3}
					name={"customer"}
					placeholder={"Type at least 3 characters to search customers..."}
					route={getSearchCustomerRoute}
					schema={customerSchema}
					setValueCleared={setCustomerCleared}
					textField={"nameWithId"}
					touched={showValidationError}
					valid={!showValidationError}
					value={quoteState.customer}
					wrapperStyle={{
						marginTop: "0px",
						minWidth: 275,
					}}
				/>
			</div>
		</div>
	) : undefined;
};
